import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows } from '@visx/grid';
import type { ReactNode } from 'react';
import { formatClock, formatCompactNumber, formatDate } from '../lib/format.ts';

/**
 * Shared chart anatomy (data-viz.md): at most 5 ticks, labels in caption size and fg-subtle
 * with tabular numerals, horizontal grid lines only in --border-subtle, no chart border, no
 * vertical grid.
 */
/** SVG plot geometry in user units, not CSS spacing: headroom, room for end labels, the x-axis band and the y-axis band. */
export const PLOT = { headroom: 8, tail: 12, xAxisBand: 24, yAxisBand: 44 };
export const MAX_TICKS = 5;

const tickLabel = {
  fill: 'var(--fg-subtle)',
  fontSize: 12,
  fontFamily: 'var(--font-sans)',
  style: { fontVariantNumeric: 'tabular-nums' },
  'data-axis-tick': '',
} as const;

/** Clock times inside a day or two, dates beyond. */
export function timeTickFormat(domain: [number, number]): (v: number) => string {
  const span = domain[1] - domain[0];
  return span <= 2 * 86_400_000 ? (v) => formatClock(v) : (v) => formatDate(v);
}

/**
 * Which bars of a band axis carry a label: every `every`-th, counted back from the last bar,
 * so the newest bar (today) is always labelled.
 */
export function bandTickShown(index: number, count: number, every: number): boolean {
  return index >= 0 && index < count && (count - 1 - index) % Math.max(1, every) === 0;
}

// visx scales carry wide generic types; the axes only need a callable scale with ticks.
type AnyScale = any;

export function YAxis({ scale, width, format = formatCompactNumber }: { scale: AnyScale; width: number; format?: (n: number) => string }): ReactNode {
  return (
    <>
      <GridRows scale={scale} width={width} numTicks={MAX_TICKS} stroke="var(--border-subtle)" strokeWidth={1} />
      <AxisLeft
        scale={scale}
        numTicks={MAX_TICKS}
        hideAxisLine
        hideTicks
        tickFormat={(v) => format(Number(v))}
        tickLabelProps={() => ({ ...tickLabel, textAnchor: 'end', dx: -4, dy: 4 })}
      />
    </>
  );
}

export function XAxis({ scale, top, format, numTicks = MAX_TICKS }: { scale: AnyScale; top: number; format: (v: AnyScale) => string; numTicks?: number }): ReactNode {
  return (
    <AxisBottom
      top={top}
      scale={scale}
      numTicks={numTicks}
      stroke="var(--border-subtle)"
      hideTicks
      tickFormat={(v) => format(v)}
      tickLabelProps={() => ({ ...tickLabel, textAnchor: 'middle', dy: 2 })}
    />
  );
}

/** A threshold or budget: a dashed --border-strong line with a text label, never a red zone. */
export function ThresholdLine({ y, width, label }: { y: number; width: number; label: string }): ReactNode {
  return (
    <g data-threshold="">
      <line x1={0} x2={width} y1={y} y2={y} stroke="var(--border-strong)" strokeDasharray="4 3" strokeWidth={1} />
      <text x={width} y={y - 4} textAnchor="end" fill="var(--fg-muted)" fontSize={12} fontFamily="var(--font-sans)">
        {label}
      </text>
    </g>
  );
}
